/**
 * crossTypePrefSpillover — static map of "when editing report type X,
 * these fields from related types are likely also relevant"
 * (Configuration Upgrade CU5).
 *
 * The Rerun Configuration step shows a small "Other report types this
 * site uses also benefit from these prefs" hint at the bottom of
 * `ReportTypePreferencesFields`. Each entry tells the wizard which
 * fields from a related report type to surface as inline cards when
 * editing the current type. Setting them writes to
 * `report.projectIntent.crossTypeSpillover` so the next rerun on the
 * related type can pre-fill from this snapshot.
 *
 * Curated, not exhaustive — the goal is to capture the small set of
 * cross-pollinated fields the LLM mentions across types (e.g. a
 * feasibility report's commercial-viability section does mention
 * NEPA pathway when there's a federal nexus, so the env type's
 * `expectedNepaPathway` belongs in feasibility's spillover map).
 */
import type { ReportTypeId } from '../config/reportTypes';

export interface CrossTypeSpilloverHint {
  /** Source report type whose field is being pulled in. */
  sourceType: ReportTypeId;
  /** Field key on `TypedPreferencesByType[sourceType]`. */
  fieldKey: string;
  /** 1-line label shown on the inline card. */
  label: string;
  /** 1-line rationale shown beneath the label. */
  rationale: string;
}

/**
 * Per-type spillover map. Reading: "when editing key X, here are the
 * related-type fields that often matter for X's narrative".
 *
 * Empty entries are allowed for types with no cross-pollination.
 */
// SSOT — the project-intent-canonical fields (projectSizeMW, customerCategory,
// inServiceDate, riskTolerance, powerSourcePreference, coolingPreference,
// PowerAvailability.powerRequirementMW, WaterResource.coolingType) are no
// longer per-report-type preferences; they live canonically on
// SiteEntity.projectIntent and `buildInputLayersBlock` (IC4) renders the
// resolved values into the prompt for every report type automatically. The
// pre-SSOT spillover hints that pointed at those fields were therefore
// redundant — removed below. The surviving hints surface report-type-only
// fields (NEPA pathway / federal nexus / discharge-water target).
export const CROSS_TYPE_SPILLOVER: Record<ReportTypeId, CrossTypeSpilloverHint[]> = {
  feasibility: [
    {
      sourceType: 'environmental',
      fieldKey: 'expectedNepaPathway',
      label: 'NEPA pathway',
      rationale: 'Feasibility commercial-viability section flags NEPA timing when a federal nexus exists.',
    },
    {
      sourceType: 'environmental',
      fieldKey: 'federalNexus',
      label: 'Federal nexus',
      rationale: 'Triggers full NEPA review across both the env and feasibility timelines.',
    },
  ],
  environmental: [],
  'test-fit': [],
  'power-availability': [],
  'water-resource': [
    {
      sourceType: 'environmental',
      fieldKey: 'dischargeWaterTarget',
      label: 'Discharge water target',
      rationale: 'Same field appears in both reports — keeping them aligned avoids contradictory narratives.',
    },
  ],
  interconnection: [],
  'supply-chain-risk': [],
  // PSR-1 (row 0.99290) — Public Sentiment Report scaffold; no
  // spillover hints in PSR-1.
  'public-sentiment': [],
  'custom-brief': [],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report; no spillover hints.
  financial: [],
  'executive-summary': [],
};

export function spilloverHintsFor(reportType: ReportTypeId): CrossTypeSpilloverHint[] {
  return CROSS_TYPE_SPILLOVER[reportType] ?? [];
}
